import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, Search } from 'lucide-react';
import { useIsAdmin } from '@/api/hooks';
import { pageDestinations, searchPages } from '@/lib/pageSearch';
import { cn } from '@/lib/utils';

const IS_MAC = typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform);

/**
 * Header page search (right-aligned in the top bar). Finds PAGES only — every link in the
 * module/admin menus (lib/pageSearch.ts) — and navigates on Enter or click. Ctrl/⌘ K focuses it
 * from anywhere. Not a record search: tickets, devices, users etc. belong in a future global search.
 */
export function PageSearch() {
  const { data: isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);

  const all = useMemo(() => pageDestinations(!!isAdmin), [isAdmin]);
  const results = useMemo(() => searchPages(all, q, 10), [all, q]);
  useEffect(() => { setCursor(0); }, [q]);

  // Ctrl/⌘ K from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        input.current?.focus();
        input.current?.select();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const go = (to: string) => {
    navigate(to);
    setQ('');
    setOpen(false);
    input.current?.blur();
  };

  return (
    <div ref={box} className="relative hidden md:block">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <input
        ref={input}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
          else if (e.key === 'Enter' && results[cursor]) { e.preventDefault(); go(results[cursor].to); }
          else if (e.key === 'Escape') { setQ(''); setOpen(false); input.current?.blur(); }
        }}
        placeholder="Search pages…"
        aria-label="Search pages"
        aria-expanded={open && !!q.trim()}
        role="combobox"
        aria-controls="page-search-results"
        className="h-8 w-36 rounded-md border border-border bg-background pl-8 pr-3 lg:pr-12 text-sm text-foreground outline-none transition-[width,box-shadow] placeholder:text-muted-foreground focus:w-64 focus:border-ring focus:ring-2 focus:ring-ring/30 lg:w-56 lg:focus:w-72"
      />
      <kbd className="pointer-events-none absolute right-2 hidden lg:block top-1/2 -translate-y-1/2 rounded border border-border bg-muted px-1 text-[10px] text-muted-foreground">
        {IS_MAC ? '⌘K' : 'Ctrl K'}
      </kbd>

      {open && q.trim() && (
        <div id="page-search-results" role="listbox"
          className="absolute right-0 z-50 mt-1.5 w-[360px] overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg">
          {results.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">
              No pages match “{q.trim()}”.
              <div className="mt-1 text-[11px]">This searches pages only — not tickets, devices or users.</div>
            </div>
          ) : (
            <ul className="max-h-[60vh] overflow-y-auto py-1">
              {results.map((d, i) => (
                <li key={`${d.to}|${d.label}`} role="option" aria-selected={i === cursor}>
                  <button type="button" onMouseEnter={() => setCursor(i)} onMouseDown={(e) => { e.preventDefault(); go(d.to); }}
                    className={cn('flex w-full items-center gap-3 px-3 py-2 text-left text-sm', i === cursor ? 'bg-muted' : 'hover:bg-muted/50')}>
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-brand-soft text-brand">
                      <d.icon className="h-3.5 w-3.5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-foreground">{d.label}</span>
                      <span className="block truncate text-[11px] text-muted-foreground">{d.group} › {d.section}</span>
                    </span>
                    {i === cursor && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
